import React, { useState, useEffect, SyntheticEvent, ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { asField } from 'informed';
import { Clear, HidePassword, ShowPassword } from '../../assets/icons';
import RightActionIcon from './right-action-icon';

const hidePasswordIcon = <HidePassword />;
const showPasswordIcon = <ShowPassword />;
const clearIcon = <Clear width="2rem" style={{ position: 'absolute', right: '1rem', top: '1.3rem', zIndex: 1 }} />;
const styledComponentClassPrefix = 'sc-';

/**
 * Assumes that classnames with capitals in them are generated by styled-components.
 * Unfortunately that's not the case. It is possible to have generated class name with
 * lowercase only. Temporarily removing item with `sc-` prefix can help to reduce the
 * possibility, but we will need to find a proper solution.
 * See https://styled-components.com/docs/faqs#why-do-my-dom-nodes-have-two-classes
 */

const removeStyledComponentsClassNames = (className: string) => {
  return className
    .split(' ')
    .filter(name => {
      return name.toLowerCase() === name && !name.startsWith(styledComponentClassPrefix);
    })
    .join(' ');
};

type Props = React.InputHTMLAttributes<HTMLInputElement> &
  Omit<IInputField, 'labeltext'> & {
    autoFocusRegardlessOfValue?: boolean;
    field?: string;
    labeltext?: string | ReactElement;
    'data-testid'?: string;
  };

// eslint-disable-next-line react/display-name
export const BasicInput = React.forwardRef((props: Props, ref: React.RefObject<HTMLInputElement>) => {
  const [enableBlur, setEnableBlur] = useState(true);
  const {
    align = 'left',
    autoFocus,
    autoFocusRegardlessOfValue,
    className = 'qa-input',
    'data-testid': dataTestid = removeStyledComponentsClassNames(className).split(' ')[0],
    clearable = false,
    componentsize,
    disabled,
    error,
    field,
    fontSize,
    labeltext,
    labelPadding,
    maxLength,
    onBlur,
    onChange,
    onClick,
    placeholder,
    type,
    value,
    width,
    ...rest
  }: Props = props;

  let symbol: any;
  const [inputType, setInputType] = useState(type);
  const showRightIcon = clearable || isPasswordType();
  const rightIcon = getRightIcon();

  if (labeltext) {
    symbol = React.isValidElement(labeltext) ? labeltext : `${labeltext}`;
  } else {
    symbol = '$';
  }

  const classForChildElements: any = (c: any): string[] => [c.concat('-label'), c.concat('-field')];

  const [labelClass, fieldClass]: string[] = classForChildElements(
    removeStyledComponentsClassNames(className).split(' ')[0]
  );
  const labelTestId = `${dataTestid}-label`;
  const fieldTestId = `${dataTestid}-field`;

  // Chrome won't follow the selection on change unless we blur and then focus it
  useEffect(() => {
    // SetTimeout allows the dom to update before we focus
    // If we don't do this the input scrolls to a character behind the current one
    const timeoutId = setTimeout(() => {
      if (value || autoFocusRegardlessOfValue) {
        setEnableBlur(false);
        if (ref && ref.current) {
          ref.current.blur();
          if (autoFocus) {
            ref.current.focus();
          }
        }
        setEnableBlur(true);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [autoFocus, ref, value, autoFocusRegardlessOfValue]);

  return (
    <InputWrapper
      data-testid={dataTestid}
      className={className}
      componentsize={componentsize}
      error={error || false}
      labeltext={labeltext}
      disabled={disabled}
      width={width}>
      {labeltext && (
        <InputLabel
          data-testid={labelTestId}
          className={labelClass}
          componentsize={componentsize}
          htmlFor={fieldClass}
          disabled={disabled}
          padding={labelPadding}>
          {symbol}
        </InputLabel>
      )}
      <InputField
        {...rest}
        data-testid={fieldTestId}
        align={align}
        className={fieldClass}
        clearable={clearable}
        componentsize={componentsize}
        error={error || false}
        // @ts-ignore doesn't look like this does anything
        field={field || fieldClass}
        fontSize={fontSize}
        id={fieldClass}
        labeltext={labeltext}
        maxlength={maxLength}
        name={field || fieldClass}
        onBlur={e => {
          if (enableBlur === false) return;
          if (onBlur) {
            onBlur(e);
          }
        }}
        onChange={e => {
          if (onChange) {
            onChange(e);
          }
        }}
        onClick={onClick}
        placeholder={placeholder}
        ref={ref}
        disabled={disabled}
        type={inputType || 'text'}
        value={value}
        width={width}
        onSelect={handleSelect}
      />
      {showRightIcon && (
        <RightActionIcon icon={rightIcon} onClick={handleRightIconClick} setEnableBlur={setEnableBlur} />
      )}
    </InputWrapper>
  );

  function isPasswordType() {
    return type === 'password';
  }

  function getRightIcon() {
    if (isPasswordType()) {
      return inputType === 'text' ? hidePasswordIcon : showPasswordIcon;
    }
    return clearIcon;
  }

  function handleRightIconClick() {
    if (isPasswordType()) {
      setInputType(inputType === 'text' ? 'password' : 'text');
      setTimeout(() => {
        if (ref?.current) {
          const length = ref.current.value.length;
          ref.current?.focus();
          ref.current?.setSelectionRange(length, length);
        }
      }, 0);
      return;
    }

    ref && ref.current && ref.current.focus();
    if (onChange) {
      onChange(
        // @ts-ignore target is missing DOM EventTarget properties
        { target: { value: '' }, type: 'change' }
      );
    }
  }

  function handleSelect(event: SyntheticEvent<HTMLInputElement>) {
    if (!isPasswordType() || inputType !== 'password') {
      return;
    }

    const target = event.target as HTMLInputElement;
    const { value, selectionStart, selectionEnd } = target;
    if (
      (selectionStart != value.length || selectionEnd != value.length) &&
      (selectionStart != 0 || selectionEnd != value.length)
    ) {
      target.setSelectionRange(value.length, value.length);
    }
  }
});

export const Input = asField<any>(({ fieldApi, forwardedRef, ...props }) => {
  const { setValue, setTouched } = fieldApi;

  const onBlur = (e: any) => {
    setTouched(true);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const onChange = (e: any) => {
    setValue(e.target.value, e);
    if (props.onChange) {
      props.onChange(e);
    }
  };
  return <BasicInput {...props} ref={forwardedRef} onBlur={onBlur} onChange={onChange} />;
});

export const HighlightableInput = styled(Input)<{ highlight: boolean; error: boolean }>`
  ${({ highlight, theme }) =>
    highlight &&
    `
      box-shadow: ${theme.shadows.lightFocus};
      ${InputLabel},
      ${InputField} {
        border-color: ${theme.colors.supporting.focusShadow};
      }
    `}

  ${({ theme, error }) =>
    error &&
    css`
      ${InputField} {
        color: ${theme.colors.status.error};
      }
    `};
`;

interface IInputWrapper {
  error?: boolean;
  labeltext: any;
  componentsize?: string;
  disabled?: boolean;
  width?: string | number;
}

const InputLabel = styled.label<{ componentsize?: string; padding?: string; disabled?: boolean }>`
  text-align: center;
  white-space: nowrap;
  ${({ disabled, theme }) => css`
    background-color: ${theme.colors.grey100};
    border: 1px solid ${disabled ? theme.colors.grey300 : theme.colors.grey450};
    border-radius: ${theme.borderRadius.small} 0 0 ${theme.borderRadius.small};
    border-right: 0;
    color: ${disabled ? theme.colors.grey300 : theme.colors.brand.quaternary};
    font-weight: 600;
  `};
  ${({ componentsize, padding }) =>
    (componentsize === 'standard' || componentsize === 'fit') &&
    css`
      min-width: 4.6rem;
      font-size: 1.6rem;
      padding: ${padding ?? '1.2rem 1.2rem 0 1.2rem'};
    `};
  ${({ componentsize, padding }) =>
    componentsize === 'compact' &&
    css`
      font-size: 1.6rem;
      padding: ${padding ?? '1.2rem 1.2rem 0 1.2rem'};
    `};
`;

const InputWrapper = styled.div<IInputWrapper>`
  padding: 0;
  margin: 0;
  display: flex;
  position: relative;

  .rightIcon {
    visibility: hidden;
  }

  &:focus-within {
    box-shadow: ${({ theme }) => theme.shadows.lightFocus};

    .rightIcon {
      visibility: visible;
    }

    ${InputLabel} {
      border-color: ${({ theme }) => theme.colors.supporting.focusShadow};
    }
  }

  ${({ theme, error }) =>
    error &&
    css`
      box-shadow: ${theme.shadows.lightred} !important;
      label {
        border-color: ${theme.colors.status.error} !important;
      }
    `};
  &:invalid {
    box-shadow: ${({ theme }) => theme.shadows.lightred};
    label {
      border-color: ${({ theme }) => theme.colors.status.error};
    }
  }
  ${({ theme, labeltext }) =>
    labeltext &&
    css`
      border-radius: ${theme.borderRadius.small};
    `};
  ${({ theme, labeltext }) =>
    !labeltext &&
    css`
      border-radius: 0 ${theme.borderRadius.small} ${theme.borderRadius.small} 0;
    `};
  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `};
  ${({ componentsize }) =>
    componentsize === 'fit' &&
    css`
      flex-basis: 100%;
    `};
`;

interface IInputField {
  align?: string;
  componentsize?: string;
  error?: boolean;
  fontSize?: string;
  labeltext?: boolean | string | ReactElement;
  labelPadding?: string;
  disabled?: boolean;
  clearable?: boolean;
}

export const InputField = styled.input<IInputField>`
  text-align: ${({ align }) => align};
  display: flex;
  width: 100%;
  ${({ theme, disabled }) => css`
    border: 1px solid ${disabled ? theme.colors.grey300 : theme.colors.grey450};
    color: ${disabled ? theme.colors.grey300 : theme.components.global.textColor};
    font-weight: 600;
    margin: 0 !important;
    &::placeholder {
      color: ${theme.colors.grey300};
    }
    &:active,
    &:active &,
    &:focus-within,
    &:focus {
      border-color: ${disabled ? theme.colors.grey300 : theme.colors.supporting.focusShadow};
    }
    &:invalid,
    &:invalid & {
      border-color: ${theme.colors.status.error};
    }
  `};
  ${({ theme, error }) =>
    error &&
    css`
      border-color: ${theme.colors.status.error} !important;
    `};
  ${({ theme, labeltext }) =>
    labeltext &&
    css`
      border-radius: 0 ${theme.borderRadius.small} ${theme.borderRadius.small} 0;
    `};
  ${({ theme, labeltext }) =>
    !labeltext &&
    css`
      border-radius: ${theme.borderRadius.small};
    `};
  ${({ componentsize, clearable }) =>
    (componentsize === 'standard' || componentsize === 'fit') &&
    css`
      font-size: 1.6rem;
      padding: 1.3rem ${clearable ? '3.75rem' : '1.3rem'} 1.3rem 1.3rem;
    `};
  ${({ componentsize, clearable }) =>
    componentsize === 'compact' &&
    css`
      font-size: 1.6rem;
      padding: 1rem ${clearable ? '3.75rem' : '1.1rem'} 1rem 1.1rem;
    `};
  ${({ fontSize }) =>
    fontSize &&
    css`
      font-size: ${fontSize} !important;
    `};

  /* stylelint-disable property-no-vendor-prefix */
  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* stylelint-enable property-no-vendor-prefix */
`;

export default Input;
